import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { GlobalConstants } from '../global-constants';
import { StripeTaxService, TaxBreakdown } from '../services/stripe-tax.service';
import { AuthService } from '../services/auth.service';

declare var $: any;

@Component({
  selector: 'app-payment',
  templateUrl: './paymentStripe.component.html',
  styleUrls: ['./payment.component.less']
})
export class PaymentComponent implements OnInit, AfterViewInit, OnDestroy {
  mode: 'new_booking' | 'existing_trip' = 'new_booking';

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  subtotal = 0;
  taxAmount = 0;
  taxPercent = 0;
  total = 0;
  taxEstimated = false;

  zipCode = '';
  private zipSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  taxCalculating = false;
  taxError: string | null = null;
  taxBreakdown: TaxBreakdown | null = null;
  taxCalcId: string | null = null;

  isProcessing = false;
  stripeReady = false;
  zipValidated = false;
  clientSecret: string | null = null;
  setupIntentId: string | null = null;

  tripId = '';
  customerId = '';
  customerName = '';

  get remainingBalance(): number {
    return this.subtotal;
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private stripeTaxService: StripeTaxService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.customerId = this.authService.getCustomerId();
    this.customerName = this.authService.getCustomerName();

    const paymentMode = localStorage.getItem('payment_mode');
    if (paymentMode === 'existing_trip') {
      this.mode = 'existing_trip';
      this.tripId = localStorage.getItem('t_id') || '';
      this.loadTripAmount();
    } else {
      const booking = JSON.parse(localStorage.getItem('booking') || '{}');
      this.subtotal = parseFloat(booking?.data?.[0]?.total_price || '0');
      this.total = this.subtotal;
    }

    this.zipSubject.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      filter(z => z.length >= 5),
      takeUntil(this.destroy$)
    ).subscribe(zip => this.calculateStripeTax(zip));
  }

  ngAfterViewInit(): void {
    this.initStripe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.zipSubject.complete();
  }

  // Initialize Stripe with Payment Element (supports card, bank, and more)
  private async initStripe(): Promise<void> {
    this.stripe = await loadStripe(GlobalConstants.Stripepkey);
    if (!this.stripe) return;
    if (this.mode === 'new_booking') {
      await this.createSetupIntentForUI();
    } else {
      await this.createPaymentIntentForUI();
    }
    // Elements are mounted only after zip code is validated
  }

  // Initializes (or reinitializes) Stripe Elements with the given clientSecret.
  // Called on page load and again whenever a new PaymentIntent is created (e.g. after tax is added).
  private async initElements(clientSecret: string): Promise<void> {
    if (!this.stripe) return;

    if (this.paymentElement) {
      this.paymentElement.unmount();
      this.paymentElement = null;
    }

    this.clientSecret = clientSecret;
    this.stripeReady = false;

    this.elements = this.stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0066cc',
          colorText: '#555555',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          spacingUnit: '4px'
        }
      }
    });

    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs',
      defaultValues: {
        billingDetails: {
          name: this.customerName,
        }
      },
      fields: {
        billingDetails: 'auto'
      }
    });

    this.paymentElement.mount('#payment-element');
    this.stripeReady = true;
  }

  // Create SetupIntent for new bookings — saves PM without charging
  private async createSetupIntentForUI(): Promise<void> {
    try {
      const res: any = await this.http.post<any>(
        GlobalConstants.apiURL + 'create_setup_intent_booking',
        { customer_id: this.customerId, customer_name: this.customerName },
        { headers: { 'Content-type': 'application/json' } }
      ).toPromise();
      if (res?.success && res?.client_secret) {
        this.clientSecret = res.client_secret;
        this.setupIntentId = res.si_id;
      }
    } catch (error) {
      console.error('Failed to create setup intent:', error);
    }
  }

  // Create Payment Intent upfront for Payment Element
  private async createPaymentIntentForUI(): Promise<void> {
    try {
      const amountToPay = this.taxEstimated ? this.total : this.subtotal;

      const payload = this.mode === 'existing_trip' && this.tripId
        ? { trip_id: this.tripId }
        : {
            amount: amountToPay,
            customer_id: this.customerId,
            customer_name: this.customerName,
            tax_calc_id: this.taxCalcId
          };

      const endpoint = this.mode === 'existing_trip' && this.tripId
        ? 'create_payment_intent'
        : 'create_pi';

      const piRes: any = await this.http.post<any>(
        GlobalConstants.apiURL + endpoint,
        payload,
        { headers: { 'Content-type': 'application/json' } }
      ).toPromise();

      if (piRes?.success && piRes?.client_secret) {
        this.clientSecret = piRes.client_secret;
      }
    } catch (error) {
      console.error('Failed to create payment intent:', error);
    }
  }

  private loadTripAmount(): void {
    const booking = JSON.parse(localStorage.getItem('booking') || '{}');
    const localAmount = parseFloat(booking?.data?.[0]?.total_price || '0');
    if (localAmount > 0) {
      this.subtotal = localAmount;
      this.total = localAmount;
    }

    if (!this.tripId) return;

    $('.preloader').show();
    this.http.get<any>(
      GlobalConstants.apiURL + `trip_details?trip_id=${this.tripId}`,
      { headers: { 'Content-type': 'application/json' } }
    ).subscribe({
      next: res => {
        $('.preloader').fadeOut();
        if (res.success && res.amount) {
          this.subtotal = parseFloat(res.amount) || this.subtotal;
          this.total = this.subtotal;
        }
      },
      error: () => $('.preloader').fadeOut()
    });
  }

  onZipChange(value: string): void {
    this.zipCode = value;
    const normalizedZip = this.stripeTaxService.normalizeZipCode(value);
    if (!this.stripeTaxService.isValidZipFormat(normalizedZip)) {
      this.zipValidated = false;
      this.taxEstimated = false;
      this.taxAmount = 0;
      this.total = this.subtotal;
      this.taxError = null;
      return;
    }
    this.zipSubject.next(normalizedZip);
  }

  private calculateStripeTax(zip: string): void {
    this.taxCalculating = true;
    this.taxError = null;

    this.stripeTaxService.calculateTripTax(
      this.subtotal,
      zip,
      this.customerId,
      this.mode === 'existing_trip' ? undefined : undefined
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: res => {
        console.log('Tax API response:', res);
        this.taxCalculating = false;
        const failed = res.success === false;
        if (failed) {
          this.taxError = res.message || 'Tax calculation failed';
          this.taxEstimated = false;
          this.taxAmount = 0;
          this.total = this.subtotal;
          this.zipValidated = true;
          if (this.clientSecret && !this.stripeReady) {
            this.initElements(this.clientSecret);
          }
          return;
        }
        this.taxBreakdown = this.stripeTaxService.formatTaxSummary(res, this.subtotal);
        this.taxCalcId = res.tax_calc_id ?? res.calc_id ?? res.calculation_id ?? this.taxBreakdown.taxId ?? null;
        this.taxAmount = this.taxBreakdown.taxAmount;
        this.total = this.taxBreakdown.total;
        this.taxPercent = this.taxBreakdown.taxPercent;
        this.taxEstimated = this.taxAmount > 0;

        if (this.taxCalcId) {
          localStorage.setItem('stripe_tax_calc_id', this.taxCalcId);
        }

        this.zipValidated = true;
        if (this.taxEstimated && this.mode !== 'new_booking') {
          this.updatePaymentIntentWithTax();
        } else if (this.clientSecret && !this.stripeReady) {
          this.initElements(this.clientSecret);
        }
      },
      error: () => {
        this.taxCalculating = false;
        this.taxError = 'Unable to calculate taxes for this location';
        this.taxEstimated = false;
        this.taxAmount = 0;
        this.total = this.subtotal;
        this.zipValidated = true;
        if (this.clientSecret && !this.stripeReady) {
          this.initElements(this.clientSecret);
        }
      }
    });
  }

  // Creates a new PaymentIntent with tax-inclusive amount and reinitializes
  // Stripe Elements so the payment form is bound to the correct secret and amount.
  // subtotal, tax_amount, and tax_calc_id are sent so the backend can store them
  // in PaymentIntent metadata['tax_calculation'] for Stripe Tax recording.
  private updatePaymentIntentWithTax(): void {
    const payload = this.mode === 'existing_trip' && this.tripId
      ? {
          trip_id: this.tripId,
          amount: this.total,
          subtotal: this.subtotal,
          tax_amount: this.taxAmount,
          tax_calc_id: this.taxCalcId
        }
      : {
          amount: this.total,
          subtotal: this.subtotal,
          tax_amount: this.taxAmount,
          customer_id: this.customerId,
          customer_name: this.customerName,
          tax_calc_id: this.taxCalcId
        };

    const endpoint = this.mode === 'existing_trip' && this.tripId
      ? 'create_payment_intent'
      : 'create_pi';

    this.stripeReady = false;

    this.http.post<any>(
      GlobalConstants.apiURL + endpoint,
      payload,
      { headers: { 'Content-type': 'application/json' } }
    ).pipe(takeUntil(this.destroy$))
    .subscribe({
      next: async (res) => {
        if (res?.success && res?.client_secret) {
          await this.initElements(res.client_secret);
        } else {
          this.stripeReady = true;
        }
      },
      error: (err) => {
        console.error('Failed to update payment intent with tax:', err);
        this.stripeReady = true;
      }
    });
  }

  async pay(): Promise<void> {
    if (!this.stripe || !this.elements || !this.paymentElement || this.isProcessing || !this.clientSecret) {
      console.error('pay() blocked:', { stripe: !!this.stripe, elements: !!this.elements, paymentElement: !!this.paymentElement, clientSecret: !!this.clientSecret });
      return;
    }
    this.isProcessing = true;
    $('.preloader').show();

    try {
      const { error: submitError } = await this.elements.submit();
      if (submitError) {
        $('.preloader').fadeOut();
        this.showError(submitError.message || 'Please check your payment details.');
        this.isProcessing = false;
        return;
      }

      const amount = this.taxEstimated ? this.total : this.subtotal;

      // Persisted so /payment-return can rebuild the correct (tax-inclusive) request
      // if Stripe redirects the browser away instead of resolving this call in-page.
      localStorage.setItem('payment_return_mode', this.mode);
      localStorage.setItem('payment_return_payable', amount.toString());
      if (this.mode === 'existing_trip') {
        localStorage.setItem('payment_return_trip_id', this.tripId);
      }

      if (this.mode === 'new_booking') {
        const result = await this.stripe.confirmSetup({
          elements: this.elements,
          clientSecret: this.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${GlobalConstants.websiteURL}#/payment-return`
          }
        });
        if (result.error) {
          console.error('Stripe error:', result.error);
          $('.preloader').fadeOut();
          this.showError(result.error.message || 'Failed to save payment method. Please try again.');
          this.isProcessing = false;
          return;
        }
        if (result.setupIntent) {
          this.confirmBooking(result.setupIntent.id, amount);
        }
        // No setupIntent means browser redirected to return_url
      } else {
        const result = await this.stripe.confirmPayment({
          elements: this.elements,
          clientSecret: this.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${GlobalConstants.websiteURL}#/payment-return`
          }
        });
        if (result.error) {
          console.error('Stripe error:', result.error);
          $('.preloader').fadeOut();
          this.showError(result.error.message || 'Payment failed. Please try again.');
          this.isProcessing = false;
          return;
        }
        if (result.paymentIntent) {
          console.log('Payment confirmed. PI:', result.paymentIntent.id, 'status:', result.paymentIntent.status);
          this.confirmExistingTripPayment(result.paymentIntent.id);
        }
        // No paymentIntent means browser will redirect to return_url
      }
    } catch (error: any) {
      console.error('pay() exception:', error);
      $('.preloader').fadeOut();
      this.showError(error?.message || 'Payment validation failed. Please check your details and try again.');
      this.isProcessing = false;
    }
  }

  private confirmBooking(setupIntentId: string, amount: number): void {
    const bookingData = JSON.parse(localStorage.getItem('booking') || '{}');

    const body = {
      setup_intent_id: setupIntentId,
      customer_id: this.customerId,
      customer_name: this.customerName,
      payable: amount,
      bookingdata: bookingData,
      tripname: localStorage.getItem('tripname') || '',
      favouritetrip: localStorage.getItem('favourite') || '',
      tax_calc_id: this.taxCalcId || null
    };

    this.http.post<any>(
      GlobalConstants.apiURL + 'confirm_booking',
      body,
      { headers: { 'Content-type': 'application/json' } }
    ).subscribe({
      next: res => {
        $('.preloader').fadeOut();
        if (res.status === 'success') {
          localStorage.removeItem('payment_mode');
          Swal.fire({
            title: 'Booking Confirmed!',
            text: 'Your payment method has been saved. Payment will be collected when your trip is completed.',
            icon: 'success',
            confirmButtonText: 'View My Bookings'
          }).then(() => {
            this.router.navigate(['/my-bookings']).then(() => window.location.reload());
          });
        } else {
          this.showError(res.message || 'Booking confirmation failed. Please contact support.');
          this.isProcessing = false;
        }
      },
      error: () => {
        $('.preloader').fadeOut();
        this.showError('Could not confirm booking. Please contact support.');
        this.isProcessing = false;
      }
    });
  }

  private confirmExistingTripPayment(paymentIntentId: string): void {
    this.http.post<any>(
      GlobalConstants.apiURL + 'confirm_payment',
      { trip_id: this.tripId, payment_intent_id: paymentIntentId, tax_calc_id: this.taxCalcId || null },
      { headers: { 'Content-type': 'application/json' } }
    ).subscribe({
      next: res => {
        $('.preloader').fadeOut();
        if (res.success) {
          localStorage.removeItem('payment_mode');
          Swal.fire({
            title: 'Payment Successful!',
            text: `Your payment of $${parseFloat(res.amount || '0').toFixed(2)} has been received.`,
            icon: 'success',
            confirmButtonText: 'View My Bookings'
          }).then(() => {
            this.router.navigate(['/my-bookings']).then(() => window.location.reload());
          });
        } else {
          this.showError(res.message || 'Payment confirmation failed. Please contact support.');
          this.isProcessing = false;
        }
      },
      error: () => {
        $('.preloader').fadeOut();
        this.showError('Could not confirm payment. Please contact support.');
        this.isProcessing = false;
      }
    });
  }

  private showError(message: string): void {
    Swal.fire({ title: 'Error', text: message, icon: 'error', confirmButtonText: 'OK' });
  }
}